import { TQuote, TGranularity } from '../types';
import { eventLog, TTickEvent } from './EventLog';
import { eventStream } from './EventStream';

export type TReplaySource = 'live' | 'history';

export type TReplayStats = {
    count: number;
    firstEpoch: number | null;
    lastEpoch: number | null;
    timeSpan: number;
    sources: { live: number; history: number };
};

export type TReplayAddOptions = {
    symbol: string;
    granularity: TGranularity;
};

export interface IReplayService {
    addHistorical(quotes: TQuote[], opts: TReplayAddOptions): void;
    addLive(quote: TQuote, opts: TReplayAddOptions): void;
    getRange(startEpoch: number, endEpoch: number): TTickEvent[];
    findClosest(epoch: number): TTickEvent | undefined;
    latest(): TTickEvent | undefined;
    iterator(options?: { startEpoch?: number; endEpoch?: number; reverse?: boolean }): Generator<TTickEvent>;
    stats(filter?: { symbol?: string; granularity?: number; source?: TReplaySource | 'both'; minEpoch?: number; maxEpoch?: number }): TReplayStats;
    sample(targetCount: number, filter?: { symbol?: string; granularity?: number; source?: TReplaySource | 'both'; minEpoch?: number; maxEpoch?: number }): TTickEvent[];
    reset(): void;
}

class ReplayService implements IReplayService {
    addHistorical(quotes: TQuote[], opts: TReplayAddOptions): void {
        const before = eventLog.size();
        eventLog.addHistorical(quotes);
        this._annotateNewEvents(before, opts.symbol, opts.granularity);
    }
    addLive(quote: TQuote, opts: TReplayAddOptions): void {
        const before = eventLog.size();
        eventLog.addLive(quote);
        this._annotateNewEvents(before, opts.symbol, opts.granularity);
    }
    getRange(startEpoch: number, endEpoch: number): TTickEvent[] { return eventLog.getRange(startEpoch, endEpoch); }
    findClosest(epoch: number): TTickEvent | undefined { return eventStream.findClosest(epoch); }
    latest(): TTickEvent | undefined { return eventLog.latest(); }
    iterator(options?: { startEpoch?: number; endEpoch?: number; reverse?: boolean }): Generator<TTickEvent> { return eventStream.iterator(options); }
    stats(filter?: { symbol?: string; granularity?: number; source?: TReplaySource | 'both'; minEpoch?: number; maxEpoch?: number }): TReplayStats { return eventStream.getStats(filter); }
    sample(targetCount: number, filter?: { symbol?: string; granularity?: number; source?: TReplaySource | 'both'; minEpoch?: number; maxEpoch?: number }): TTickEvent[] { return eventStream.sample(targetCount, filter); }
    reset(): void {
        // Clearing is not yet exposed in EventLog; emulate by re-instantiating internal arrays.
        // Since EventLog is a singleton, we manually drop references via splice.
        const all = eventLog.getAll();
        all.splice(0, all.length); // not effective outside; placeholder for future clear method.
        // TODO: introduce explicit clear method on EventLog if needed.
    }
    private _annotateNewEvents(startIndex: number, symbol: string, granularity: number) {
        const all = eventLog.getAll();
        for (let i = startIndex; i < all.length; i++) {
            const ev = all[i];
            ev.symbol = symbol;
            ev.granularity = granularity;
        }
    }
}

export const replayService: IReplayService = new ReplayService();
